// Lightweight per-page SEO/OG meta updater (no Helmet dependency).
// Updates <title>, description, canonical, OG/Twitter meta, and injects
// optional JSON-LD structured data blocks.
import { useEffect } from "react";

const CANONICAL_ORIGIN = "https://melbiwellness.com";
const JSONLD_ID_PREFIX = "melbi-jsonld-";

function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, key, name] = selector.match(/\[(.+?)="(.+?)"\]/) || [];
    if (key && name) el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setCanonical(url) {
  const href = url.startsWith("http")
    ? url
    : `${CANONICAL_ORIGIN}${url.startsWith("/") ? url : `/${url}`}`;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  // Keep og:url in sync with the canonical for consistency.
  setMeta('meta[property="og:url"]', "content", href);
}

function setJsonLd(blocks) {
  // Remove any previously-injected JSON-LD blocks from prior pages.
  document.head
    .querySelectorAll(`script[data-testid^="${JSONLD_ID_PREFIX}"]`)
    .forEach((n) => n.remove());
  if (!blocks || !blocks.length) return;
  blocks.forEach((data, idx) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-testid", `${JSONLD_ID_PREFIX}${idx}`);
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  });
}

export default function useSEO({ title, description, image, url, jsonLd }) {
  // Build-time prerender (no DOM): record tags so scripts/prerender.js can bake them into HTML.
  if (typeof window === "undefined") {
    globalThis.__MELBI_SEO__ = { title, description, image, url, jsonLd };
  }
  useEffect(() => {
    if (title) {
      document.title = title;
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (image) {
      setMeta('meta[property="og:image"]', "content", image);
      setMeta('meta[name="twitter:image"]', "content", image);
    }
    if (url) setCanonical(url);
    setJsonLd(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : null);
  }, [title, description, image, url, jsonLd]);
}
